import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useUserContext } from "../contexts/UserContext";
import { useUIStateContext } from "../contexts/UIStateContext";
import { handleSetupSelection } from "../pages/LandingPage/helpers";
import {
  DEPARTMENTS,
  IMPLEMENTED_SETUP_KEYS,
} from "../pages/LandingPage/constants";
import { navigateToHome } from "../routes/navigation";

interface DeepLinkParams extends Record<string, string | undefined> {
  department?: string;
  setupKey?: string;
}

const VALID_DEPARTMENTS = new Set(Object.values(DEPARTMENTS));

/**
 * Handles explicit /:department/:setupKey deep links.
 *
 * Deep links are used as an entry point to select a setup. When a valid
 * deep link is provided, the URL remains unchanged so the setup can continue
 * to be accessed directly through that URL.
 *
 * Normal setup selection does not update the URL and is handled separately
 * through handleSetupSelection.
 */
export function useDeepLinkSetup(): { isDeepLink: boolean } {
  const { department: deptParam, setupKey } = useParams<DeepLinkParams>();
  const navigate = useNavigate();
  const location = useLocation();

  const hasApplied = useRef(false);

  const {
    selected,
    defaultStore,
    setSelected,
    setupListNavInfoByDepartment,
    iamUserState,
    permissions,
    setPermissions,
    isFetchingUserData,
  } = useUserContext();

  const { setLandingPageToast } = useUIStateContext();

  const isDeepLink = Boolean(deptParam && setupKey);

  // Reset the guard when navigating to a different deep-link URL.
  useEffect(() => {
    hasApplied.current = false;
  }, [deptParam, setupKey]);

  useEffect(() => {
    if (!isDeepLink || isFetchingUserData || hasApplied.current) {
      return;
    }

    // Wait until setup navigation information is available.
    if (Object.keys(setupListNavInfoByDepartment).length === 0) {
      return;
    }

    const department = deptParam!.toLowerCase();
    const setup = setupKey!;

    // Redirect invalid deep links to home.
    if (!VALID_DEPARTMENTS.has(department)) {
      navigateToHome({
        navigate,
        search: location.search,
        replace: true,
      });
      return;
    }

    if (!IMPLEMENTED_SETUP_KEYS.includes(setup)) {
      navigateToHome({
        navigate,
        search: location.search,
        replace: true,
      });
      return;
    }

    const departmentSetups =
      setupListNavInfoByDepartment[department]?.setups ?? [];

    const matchedSetup = departmentSetups.find(({ id }) => id === setup);

    // Setup is not available for this department/user.
    if (!matchedSetup) {
      navigateToHome({
        navigate,
        search: location.search,
        replace: true,
      });
      return;
    }

    // Setup is already selected.
    if (
      selected.department.value === department &&
      selected.setup.value === setup
    ) {
      hasApplied.current = true;
      return;
    }

    const success = handleSetupSelection({
      setup: {
        value: matchedSetup.id,
        label: matchedSetup.text,
      },
      department: {
        value: department,
        label: matchedSetup.department?.label ?? department,
      },
      store: defaultStore,
      setupListNavInfoByDepartment,
      setLandingPageToast,
      setSelected,
      iamUserState,
      permissions,
      setPermissions,
    });

    // handleSetupSelection handles the failure state.
    if (!success) {
      return;
    }

    // The setup has been initialized successfully.
    // Keep the explicit deep-link URL unchanged.
    hasApplied.current = true;
  }, [
    isDeepLink,
    isFetchingUserData,
    setupListNavInfoByDepartment,
    deptParam,
    setupKey,
    selected.department.value,
    selected.setup.value,
    location.search,
    navigate,
  ]);

  return { isDeepLink };
}
